import type { AnalyticsMode } from '@/api/types';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';

/**
 * Toggle option interface for data-driven mode selection.
 * Supports feature flag control via the hidden property.
 */
export interface ToggleOption {
    /** The value to emit when this option is selected */
    value: AnalyticsMode;
    /** Display label for the option */
    label: string;
    /** When true, this option is not rendered (for feature flag control) */
    hidden?: boolean;
}

/**
 * Default options for backward compatibility.
 * Used when no options prop is provided.
 */
const DEFAULT_OPTIONS: ToggleOption[] = [
    { value: 'value', label: 'Value' },
    { value: 'pnl', label: 'P&L' },
];

interface ModeToggleProps {
    /** Currently selected mode */
    value: AnalyticsMode;
    /** Callback when mode changes */
    onChange: (mode: AnalyticsMode) => void;
    /**
     * Array of toggle options to display.
     * Each option can be hidden via the hidden property (for feature flag control).
     * If not provided, defaults to Value and P&L options.
     */
    options?: ToggleOption[];
    /**
     * @deprecated Use options prop with hidden property instead.
     * Optional list of mode options to hide (e.g., ['pnl'] for debt ledgers)
     */
    hideOptions?: AnalyticsMode[];
    /**
     * Optional localStorage key for persisting selection.
     * When provided, clicking an option will save the value to localStorage.
     */
    storageKey?: string;
    /**
     * Default value when no selection exists.
     * Note: The value prop takes precedence for rendering.
     * This prop is provided for API compatibility and future controlled/uncontrolled patterns.
     */
    defaultValue?: AnalyticsMode;
}

/**
 * Toggle component for switching between analytics modes.
 * Styled as a button group following the TypeFilter pattern.
 *
 * Features:
 * - Data-driven options via the options prop
 * - Hidden options via hidden property (for feature flag control)
 * - Backward compatibility with hideOptions prop
 * - Optional localStorage persistence via storageKey
 * - Configurable defaultValue (though value prop takes precedence)
 *
 * If only one option remains visible, the toggle is still rendered
 * but the single option appears as selected and disabled.
 */
export function ModeToggle({
    value,
    onChange,
    options,
    hideOptions = [],
    storageKey,
    // defaultValue is accepted for API compatibility but not used directly
    // The parent component manages state using this as the initial value
    defaultValue: _defaultValue,
}: ModeToggleProps) {
    // Determine which options to use
    // Priority: options prop > default options with hideOptions filtering
    let visibleOptions: ToggleOption[];

    if (options) {
        // Use provided options, filter out hidden ones
        visibleOptions = options.filter((option) => !option.hidden);
    } else {
        // Use default options, filter out hideOptions (backward compatibility)
        visibleOptions = DEFAULT_OPTIONS.filter(
            (option) => !hideOptions.includes(option.value)
        );
    }

    // If no options are visible, don't render anything
    if (visibleOptions.length === 0) {
        return null;
    }

    // Handle option click
    const handleClick = (optionValue: AnalyticsMode) => {
        onChange(optionValue);

        // Persist to localStorage if storageKey is provided
        if (storageKey) {
            try {
                localStorage.setItem(storageKey, optionValue);
            } catch {
                // localStorage might be unavailable
            }
        }
    };

    // If only one option, still show it but disabled/selected
    if (visibleOptions.length === 1) {
        const singleOption = visibleOptions[0]!;
        return (
            <div className={TOGGLE_STYLES.container}>
                <button
                    type="button"
                    className={`px-4 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${TOGGLE_STYLES.active}`}
                    aria-pressed={true}
                    disabled
                >
                    {singleOption.label}
                </button>
            </div>
        );
    }

    return (
        <div className={TOGGLE_STYLES.container}>
            {visibleOptions.map((option) => {
                const isActive = value === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => handleClick(option.value)}
                        className={`
                            px-4 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE}
                            ${isActive ? TOGGLE_STYLES.active : TOGGLE_STYLES.inactive}
                        `}
                        aria-pressed={isActive}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
